import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import SunCalc from 'suncalc3'
import { useDevModeStore, AnimatableSettings, ANIMATABLE_KEYS } from '../stores/useDevModeStore'
import { DELHI_LOCATION, SIMULATION_DATE } from '../utils/solarCalculations'

// Color interpolation helpers
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 }
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((x) => {
        const hex = Math.round(Math.max(0, Math.min(255, x))).toString(16)
        return hex.length === 1 ? '0' + hex : hex
      })
      .join('')
  )
}

function lerpColor(colorA: string, colorB: string, t: number): string {
  const a = hexToRgb(colorA)
  const b = hexToRgb(colorB)
  return rgbToHex(a.r + (b.r - a.r) * t, a.g + (b.g - a.g) * t, a.b + (b.b - a.b) * t)
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

// Interpolate between two presets
function interpolateSettings(
  from: AnimatableSettings,
  to: AnimatableSettings,
  t: number
): Partial<AnimatableSettings> {
  const result: Partial<AnimatableSettings> = {}

  for (const key of ANIMATABLE_KEYS) {
    const fromVal = from[key]
    const toVal = to[key]

    if (typeof fromVal === 'string' && typeof toVal === 'string') {
      ;(result as Record<string, string>)[key] = lerpColor(fromVal, toVal, t)
    } else if (typeof fromVal === 'number' && typeof toVal === 'number') {
      ;(result as Record<string, number>)[key] = lerp(fromVal, toVal, t)
    }
  }

  return result
}

// Compute solar-based segment boundaries for the simulation date.
// Converts SunCalc3 event times to progress values (progress=0 is 6:00 AM).
function computeSolarSegments() {
  const noon = new Date(SIMULATION_DATE + 'T12:00:00')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const times = (SunCalc as any).getSunTimes(noon, DELHI_LOCATION.latitude, DELHI_LOCATION.longitude)

  // Convert a UTC Date to progress using local time hours.
  // progress = ((localHours - 6) + 24) % 24 / 24
  function toProgress(date: Date): number {
    const localH = date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600
    return ((localH - 6) + 24) % 24 / 24
  }

  const dawn = toProgress(times.sunriseStart.value)   // sunrise
  const day = toProgress(times.solarNoon.value)        // solar noon
  const dusk = toProgress(times.sunsetEnd.value)        // sunset
  const night = toProgress(times.nadir.value)           // midnight (nadir)

  return { dawn, day, dusk, night }
}

type PresetName = 'dawn' | 'day' | 'dusk' | 'night'

// Build segments from solar times
const solar = computeSolarSegments()

const SEGMENTS: { start: number; length: number; from: PresetName; to: PresetName }[] = [
  { start: solar.dawn, length: solar.day - solar.dawn, from: 'dawn', to: 'day' },
  { start: solar.day, length: solar.dusk - solar.day, from: 'day', to: 'dusk' },
  { start: solar.dusk, length: solar.night - solar.dusk, from: 'dusk', to: 'night' },
  { start: solar.night, length: (1 - solar.night) + solar.dawn, from: 'night', to: 'dawn' },
]

// Export progress boundaries so DayNightToggle can use them for phase labels
export const SOLAR_BOUNDARIES = solar

function findSegment(progress: number) {
  for (let i = SEGMENTS.length - 1; i >= 0; i--) {
    const seg = SEGMENTS[i]
    if (i === SEGMENTS.length - 1) {
      // Last segment wraps around (night → dawn)
      if (progress >= seg.start || progress < SEGMENTS[0].start) {
        return seg
      }
    } else if (progress >= seg.start) {
      return seg
    }
  }
  return SEGMENTS[0]
}

export function useDayNightAnimation() {
  const lastTimeRef = useRef<number>(0)
  const rafIdRef = useRef<number>(0)

  // Only subscribe to the playing flag — everything else is read per-frame via getState()
  const dayNightPlaying = useDevModeStore((s) => s.dayNightPlaying)

  useEffect(() => {
    if (!dayNightPlaying) {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = 0
      }
      return
    }

    lastTimeRef.current = performance.now()

    const animate = (currentTime: number) => {
      const deltaTime = (currentTime - lastTimeRef.current) / 1000
      lastTimeRef.current = currentTime

      const state = useDevModeStore.getState()
      if (!state.dayNightPlaying) return

      const presets: Record<PresetName, AnimatableSettings> = {
        dawn: state.dawnPreset,
        day: state.dayPreset,
        dusk: state.duskPreset,
        night: state.nightPreset,
      }

      // Update progress (0 -> 1, then loop back to 0)
      let newProgress = state.dayNightProgress + deltaTime / state.dayNightDuration
      if (newProgress >= 1) {
        newProgress = newProgress % 1
      }

      state.setDayNightProgress(newProgress)

      // Find which segment we're in
      const segment = findSegment(newProgress)

      // Calculate local t within segment (0 to 1)
      let localProgress: number
      if (segment.from === 'night' && segment.to === 'dawn') {
        // Wrapping segment: night → dawn
        const progressInSeg = newProgress >= segment.start
          ? newProgress - segment.start
          : (1 - segment.start) + newProgress
        localProgress = progressInSeg / segment.length
      } else {
        localProgress = (newProgress - segment.start) / segment.length
      }
      let t = Math.max(0, Math.min(1, localProgress))

      // Apply easing or threshold per-segment
      if (state.dayNightLerpMode === 'smooth') {
        const easeFn = gsap.parseEase('power1.inOut')
        if (easeFn) {
          t = easeFn(t)
        }
      } else {
        const steps = state.dayNightThresholdSteps
        t = Math.round(t * steps) / steps
      }

      // Interpolate between the two bounding keyframes
      const interpolated = interpolateSettings(presets[segment.from], presets[segment.to], t)

      // Override azimuth with continuous 360° rotation
      const baseAzimuth = (presets.day.directionalAzimuth - 90 + 360) % 360
      const azimuth = (baseAzimuth + newProgress * 360) % 360
      ;(interpolated as Record<string, unknown>).directionalAzimuth = azimuth

      state.applyAnimatedValues(interpolated)

      rafIdRef.current = requestAnimationFrame(animate)
    }

    rafIdRef.current = requestAnimationFrame(animate)

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = 0
      }
    }
  }, [dayNightPlaying])
}
